import { message, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';
import {
  buildInformerApiPayload,
  CABINET_INFORMER_TEMPLATES_MAX,
  cloneTelegramForApi,
  getInformerEditorPlainText,
  MARKETING_SECTION_DIRTY_INITIAL,
  normalizeInformerPublications,
  normalizeInformerTargetClientIds,
  normalizeInformerTemplateRow,
  normalizeTelegramFromApi,
  pickCabinetDefaultsSavedRoot,
} from '../utils/cabinetMarketingUtils';

const useCabinetMarketingSettings = () => {
  const [loading, setLoading] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(() => ({ ...MARKETING_SECTION_DIRTY_INITIAL }));
  const [sectionSaving, setSectionSaving] = useState(() => ({ ...MARKETING_SECTION_DIRTY_INITIAL }));
  const sectionDirtyRef = useRef({ ...MARKETING_SECTION_DIRTY_INITIAL });
  const loadSeqRef = useRef(0);

  const cabinetInformerEditorRef = useRef(null);
  const [informerClearPublicationsLoading, setInformerClearPublicationsLoading] = useState(false);
  const [informerClientSelectOptions, setInformerClientSelectOptions] = useState([]);
  const [informerClientsListLoading, setInformerClientsListLoading] = useState(false);
  const [informerTargetClientLabels, setInformerTargetClientLabels] = useState({});

  const [cabinetInformer, setCabinetInformer] = useState({
    enabled: false,
    text: '',
    version: 1,
    targetClientIds: [],
    dismissible: true,
  });
  const [cabinetInformerTemplates, setCabinetInformerTemplates] = useState([]);
  const [cabinetInformerPublications, setCabinetInformerPublications] = useState([]);
  const [informerEditingTemplateId, setInformerEditingTemplateId] = useState(null);
  const [informerTemplateToggleLoadingId, setInformerTemplateToggleLoadingId] = useState(null);
  const [informerTemplateDismissibleLoadingId, setInformerTemplateDismissibleLoadingId] = useState(null);
  const [informerTemplateBodyExpanded, setInformerTemplateBodyExpanded] = useState({});
  const [informerLinkModalOpen, setInformerLinkModalOpen] = useState(false);
  const [informerLinkDraft, setInformerLinkDraft] = useState('');
  const informerLinkSavedRangeRef = useRef(null);
  const informerEmojiSavedRangeRef = useRef(null);
  const [informerEmojiPickerOpen, setInformerEmojiPickerOpen] = useState(false);
  const [informerSaveTemplateModalOpen, setInformerSaveTemplateModalOpen] = useState(false);
  const [informerSaveTemplateNameDraft, setInformerSaveTemplateNameDraft] = useState('');

  const cabinetInformerRef = useRef({
    enabled: false,
    text: '',
    version: 1,
    targetClientIds: [],
    dismissible: true,
  });
  const cabinetInformerTemplatesRef = useRef([]);
  const cabinetInformerPublicationsRef = useRef([]);

  const cabinetTelegramRef = useRef(normalizeTelegramFromApi({}));
  const [cabinetTelegram, setCabinetTelegram] = useState(() => normalizeTelegramFromApi({}));

  const patchCabinetTelegram = useCallback((updater) => {
    setCabinetTelegram((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      cabinetTelegramRef.current = next;
      return next;
    });
  }, []);

  const markSectionDirty = useCallback((section) => {
    setSectionDirty((prev) => ({ ...prev, [section]: true }));
  }, []);

  useEffect(() => {
    sectionDirtyRef.current = sectionDirty;
  }, [sectionDirty]);

  useEffect(() => {
    cabinetInformerRef.current = cabinetInformer;
    cabinetInformerTemplatesRef.current = cabinetInformerTemplates;
    cabinetInformerPublicationsRef.current = cabinetInformerPublications;
    cabinetTelegramRef.current = cabinetTelegram;
  }, [cabinetInformer, cabinetInformerTemplates, cabinetInformerPublications, cabinetTelegram]);

  const applyInformerFromApi = useCallback((informerRaw) => {
    const informer = informerRaw && typeof informerRaw === 'object' ? informerRaw : {};
    const nextInformerState = {
      enabled: !!informer.enabled,
      text: informer.text || '',
      version: Number(informer.version || 1),
      targetClientIds: normalizeInformerTargetClientIds(
        informer.target_client_ids !== undefined && informer.target_client_ids !== null
          ? informer.target_client_ids
          : informer.targetClientIds,
      ),
      dismissible: informer.dismissible !== false,
    };
    cabinetInformerRef.current = nextInformerState;
    setCabinetInformer(nextInformerState);
    setInformerTargetClientLabels({});
    const infTemplates = Array.isArray(informer.templates) ? informer.templates : [];
    const nextTemplates = infTemplates.map((t) => normalizeInformerTemplateRow(t));
    cabinetInformerTemplatesRef.current = nextTemplates;
    setCabinetInformerTemplates(nextTemplates);
    const nextPublications = normalizeInformerPublications(informer.publications);
    cabinetInformerPublicationsRef.current = nextPublications;
    setCabinetInformerPublications(nextPublications);
  }, []);

  const applyMarketingDefaultsResponse = useCallback((data) => {
    const safe = data && typeof data === 'object' ? data : {};
    if (safe.informer) {
      applyInformerFromApi(safe.informer);
    }
    if (safe.telegram_notifications) {
      const nextTg = normalizeTelegramFromApi(safe.telegram_notifications);
      patchCabinetTelegram(nextTg);
    }
    setSectionDirty({ ...MARKETING_SECTION_DIRTY_INITIAL });
  }, [applyInformerFromApi, patchCabinetTelegram]);

  const applyMarketingSliceFromSaved = useCallback((data, section) => {
    const safe = data && typeof data === 'object' ? data : {};
    if (section === 'informer' && safe.informer) {
      applyInformerFromApi(safe.informer);
      setSectionDirty((prev) => ({ ...prev, informer: false }));
    } else if ((section === 'broadcasts' || section === 'notifications') && safe.telegram_notifications) {
      patchCabinetTelegram(normalizeTelegramFromApi(safe.telegram_notifications));
      setSectionDirty((prev) => ({ ...prev, [section]: false }));
    }
  }, [applyInformerFromApi, patchCabinetTelegram]);

  const handleLoadMarketingDefaults = useCallback(async (opts) => {
    const force = opts && opts.force === true;
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      const response = await apiService.getCabinetDefaults();
      if (seq !== loadSeqRef.current) return;
      if (!force && Object.values(sectionDirtyRef.current).some(Boolean)) return;
      applyMarketingDefaultsResponse(response?.data?.data || response?.data || {});
    } catch (_error) {
      if (seq !== loadSeqRef.current) return;
      message.error('Ошибка загрузки настроек маркетинга');
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [applyMarketingDefaultsResponse]);

  useEffect(() => {
    handleLoadMarketingDefaults();
  }, [handleLoadMarketingDefaults]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInformerClientsListLoading(true);
      try {
        const res = await apiService.getClients();
        const raw = res?.data;
        const rows = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        setInformerClientSelectOptions(
          rows.map((c) => ({
            value: Number(c.id),
            label: `${c.login || '—'} (#${c.id})`,
          })),
        );
      } catch (_e) {
        if (!cancelled) setInformerClientSelectOptions([]);
      } finally {
        if (!cancelled) setInformerClientsListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    const next = cabinetInformer.text ?? '';
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [cabinetInformer.text]);

  const mergeInformerAudienceOptions = useCallback((selectedIds) => {
    const byValue = new Map();
    for (const o of informerClientSelectOptions) {
      byValue.set(o.value, o);
    }
    for (const id of selectedIds || []) {
      if (!byValue.has(id)) {
        byValue.set(id, {
          value: id,
          label: informerTargetClientLabels[id] || `Клиент #${id}`,
        });
      }
    }
    return [...byValue.values()];
  }, [informerClientSelectOptions, informerTargetClientLabels]);

  const informerAllTargetIdsKey = useMemo(() => {
    const s = new Set();
    for (const id of cabinetInformer.targetClientIds || []) {
      s.add(id);
    }
    for (const t of cabinetInformerTemplates) {
      for (const id of t.targetClientIds || []) {
        s.add(id);
      }
    }
    return [...s].sort((a, b) => a - b).join(',');
  }, [cabinetInformer.targetClientIds, cabinetInformerTemplates]);

  const telegramBroadcastAllTargetIdsKey = useMemo(() => {
    const s = new Set();
    for (const b of cabinetTelegram.broadcasts || []) {
      for (const id of b.target_client_ids || []) {
        s.add(id);
      }
    }
    return [...s].sort((a, b) => a - b).join(',');
  }, [cabinetTelegram.broadcasts]);

  useEffect(() => {
    const raw =
      cabinetTelegram.broadcasts?.flatMap((b) => (Array.isArray(b.target_client_ids) ? b.target_client_ids : [])) ||
      [];
    const idSet = [...new Set(raw.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n) && n > 0))];
    if (!idSet.length) return undefined;
    let cancelled = false;
    (async () => {
      await Promise.all(
        idSet.map(async (id) => {
          try {
            const r = await apiService.getClient(id);
            if (cancelled) return;
            const login = r?.data?.login;
            if (!login) return;
            setInformerTargetClientLabels((prev) => {
              if (prev[id]) return prev;
              return { ...prev, [id]: `${login} (#${id})` };
            });
          } catch (_err) {
            /* ignore */
          }
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [telegramBroadcastAllTargetIdsKey, cabinetTelegram.broadcasts]);

  useEffect(() => {
    const idSet = new Set();
    for (const id of cabinetInformer.targetClientIds || []) {
      idSet.add(id);
    }
    for (const t of cabinetInformerTemplates) {
      for (const id of t.targetClientIds || []) {
        idSet.add(id);
      }
    }
    const ids = [...idSet];
    if (!ids.length) return undefined;
    let cancelled = false;
    (async () => {
      await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await apiService.getClient(id);
            if (cancelled) return;
            const login = r?.data?.login;
            if (!login) return;
            setInformerTargetClientLabels((prev) => {
              if (prev[id]) return prev;
              return { ...prev, [id]: `${login} (#${id})` };
            });
          } catch (_err) {
            /* ignore */
          }
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [informerAllTargetIdsKey, cabinetInformer.targetClientIds, cabinetInformerTemplates]);

  const persistInformerSettings = useCallback(
    async (informerPatch = {}) => {
      const payload = {
        informer: buildInformerApiPayload(
          {
            informerRef: cabinetInformerRef,
            templatesRef: cabinetInformerTemplatesRef,
            editorRef: cabinetInformerEditorRef,
          },
          {
            publish_to_clients: false,
            bump_version: false,
            ...informerPatch,
          },
        ),
      };
      const response = await apiService.setCabinetDefaults(payload);
      const saved = pickCabinetDefaultsSavedRoot(response, payload);
      if (!saved?.informer) {
        throw new Error('Invalid cabinet defaults response');
      }
      applyMarketingSliceFromSaved(saved, 'informer');
      return saved;
    },
    [applyMarketingSliceFromSaved],
  );

  const handleSaveMarketingSection = useCallback(
    async (section) => {
      try {
        setSectionSaving((s) => ({ ...s, [section]: true }));
        let payload = {};
        if (section === 'informer') {
          payload = {
            informer: buildInformerApiPayload({
              informerRef: cabinetInformerRef,
              templatesRef: cabinetInformerTemplatesRef,
              editorRef: cabinetInformerEditorRef,
            }),
          };
        } else if (section === 'broadcasts' || section === 'notifications') {
          payload = {
            telegram_notifications: cloneTelegramForApi(cabinetTelegramRef.current),
          };
        }
        const response = await apiService.setCabinetDefaults(payload);
        const saved = response?.data?.data ?? payload;
        applyMarketingSliceFromSaved(saved, section);
        message.success(
          section === 'informer'
            ? 'Информер сохранён'
            : section === 'broadcasts'
              ? 'Рассылки сохранены'
              : 'Уведомления Telegram сохранены',
        );
      } catch (_error) {
        message.error('Не удалось сохранить');
      } finally {
        setSectionSaving((s) => ({ ...s, [section]: false }));
      }
    },
    [applyMarketingSliceFromSaved],
  );

  const handleClearInformerPublicationsClick = useCallback(() => {
    Modal.confirm({
      title: 'Очистить отправленные сообщения информера?',
      content:
        'Из настроек ЛК будут удалены все выпуски из истории (прошлые «Отправить»). Старые плашки у клиентов пропадут после обновления данных. Текст в поле не стирается; если информер включён и текст не пустой, API по-прежнему может отдать этот текст как единственное сообщение.',
      okText: 'Очистить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          setInformerClearPublicationsLoading(true);
          const response = await apiService.setCabinetDefaults({
            informer: { clear_publications: true },
          });
          const saved = response?.data?.data ?? {};
          applyMarketingSliceFromSaved(saved, 'informer');
          message.success('История сообщений информера очищена');
        } catch (_err) {
          message.error('Не удалось очистить историю');
          throw _err;
        } finally {
          setInformerClearPublicationsLoading(false);
        }
      },
    });
  }, [applyMarketingSliceFromSaved]);

  const handleUpdateInformerTemplate = useCallback((index, patch) => {
    setCabinetInformerTemplates((prev) => {
      const next = prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
      cabinetInformerTemplatesRef.current = next;
      return next;
    });
  }, []);

  const clearInformerEditorDraft = useCallback(() => {
    const el = cabinetInformerEditorRef.current;
    if (el) {
      el.innerHTML = '';
    }
    setCabinetInformer((prev) => {
      const next = { ...prev, text: '' };
      cabinetInformerRef.current = next;
      return next;
    });
    setInformerEditingTemplateId(null);
  }, []);

  const getInformerRootEnabled = useCallback(
    () => cabinetInformerTemplatesRef.current.some((item) => item.enabled !== false),
    [],
  );

  const persistInformerTemplatesSnapshot = useCallback(async () => {
    await persistInformerSettings({ enabled: getInformerRootEnabled() });
  }, [getInformerRootEnabled, persistInformerSettings]);

  const handleAddInformerTemplate = useCallback(async () => {
    const tempId = `tmp_inf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const nextTemplates = [
      ...cabinetInformerTemplatesRef.current,
      {
        id: tempId,
        name: '',
        text: '',
        enabled: false,
        dismissible: true,
        starts_at: null,
        ends_at: null,
        targetClientIds: [],
      },
    ];
    cabinetInformerTemplatesRef.current = nextTemplates;
    setCabinetInformerTemplates(nextTemplates);
    setInformerTemplateToggleLoadingId(tempId);
    try {
      await persistInformerTemplatesSnapshot();
      message.success('Шаблон добавлен');
    } catch (_error) {
      message.error('Не удалось сохранить новый шаблон');
    } finally {
      setInformerTemplateToggleLoadingId(null);
    }
  }, [persistInformerTemplatesSnapshot]);

  const handleDeleteInformerTemplate = useCallback(
    async (index) => {
      const list = cabinetInformerTemplatesRef.current;
      const tplId = list[index]?.id || `idx-${index}`;
      const nextTemplates = list.filter((_, idx) => idx !== index);
      cabinetInformerTemplatesRef.current = nextTemplates;
      setCabinetInformerTemplates(nextTemplates);
      setInformerTemplateToggleLoadingId(tplId);
      try {
        await persistInformerSettings({ enabled: nextTemplates.some((item) => item.enabled !== false) });
        message.success('Шаблон удалён');
      } catch (_error) {
        message.error('Не удалось удалить шаблон');
      } finally {
        setInformerTemplateToggleLoadingId(null);
      }
    },
    [persistInformerSettings],
  );

  const handlePersistInformerTemplateRow = useCallback(
    async (index, patch = null) => {
      const list = cabinetInformerTemplatesRef.current;
      if (index < 0 || index >= list.length) return;
      if (patch && typeof patch === 'object') {
        const nextTemplates = list.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
        cabinetInformerTemplatesRef.current = nextTemplates;
        setCabinetInformerTemplates(nextTemplates);
      }
      const tplId = cabinetInformerTemplatesRef.current[index]?.id || `idx-${index}`;
      const dismissibleOnlyPatch =
        patch
        && typeof patch === 'object'
        && Object.keys(patch).length === 1
        && Object.prototype.hasOwnProperty.call(patch, 'dismissible');
      const setRowLoading = dismissibleOnlyPatch
        ? setInformerTemplateDismissibleLoadingId
        : setInformerTemplateToggleLoadingId;
      setRowLoading(tplId);
      try {
        await persistInformerTemplatesSnapshot();
      } catch (_error) {
        message.error('Не удалось сохранить изменения шаблона');
      } finally {
        setRowLoading(null);
      }
    },
    [persistInformerTemplatesSnapshot],
  );

  const handleInformerEditorInput = useCallback(() => {
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    setCabinetInformer((prev) => {
      const next = { ...prev, text: el.innerHTML };
      cabinetInformerRef.current = next;
      return next;
    });
    if (informerEditingTemplateId) {
      markSectionDirty('informer');
    }
  }, [informerEditingTemplateId, markSectionDirty]);

  const captureInformerLinkEditorSelection = useCallback(() => {
    informerLinkSavedRangeRef.current = null;
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    informerLinkSavedRangeRef.current = range.cloneRange();
  }, []);

  const captureInformerEmojiEditorSelection = useCallback(() => {
    informerEmojiSavedRangeRef.current = null;
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    informerEmojiSavedRangeRef.current = range.cloneRange();
  }, []);

  const handleInsertInformerEmoji = useCallback(
    (emoji) => {
      if (!emoji) return;
      const el = cabinetInformerEditorRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      const saved = informerEmojiSavedRangeRef.current;
      if (
        saved
        && saved.commonAncestorContainer
        && saved.commonAncestorContainer.isConnected
        && el.contains(saved.commonAncestorContainer)
      ) {
        try {
          sel.removeAllRanges();
          sel.addRange(saved);
        } catch (_e) {
          /* устаревший Range */
        }
      }
      try {
        document.execCommand('insertText', false, emoji);
      } catch (_e) {
        document.execCommand('insertHTML', false, emoji);
      }
      setCabinetInformer((prev) => {
        const next = { ...prev, text: el.innerHTML };
        cabinetInformerRef.current = next;
        return next;
      });
      if (informerEditingTemplateId) {
        markSectionDirty('informer');
      }
      setInformerEmojiPickerOpen(false);
    },
    [informerEditingTemplateId, markSectionDirty],
  );

  const handleInformerEmojiPickerOpenChange = useCallback((open) => {
    if (open) {
      captureInformerEmojiEditorSelection();
    } else {
      informerEmojiSavedRangeRef.current = null;
    }
    setInformerEmojiPickerOpen(open);
  }, [captureInformerEmojiEditorSelection]);

  const runInformerEditorCommand = useCallback((command, value = null) => {
    try {
      document.execCommand('styleWithCSS', false, false);
    } catch (_e) {
      /* ignore */
    }
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false, value);
    setCabinetInformer((prev) => {
      const next = { ...prev, text: el.innerHTML };
      cabinetInformerRef.current = next;
      return next;
    });
    markSectionDirty('informer');
  }, [markSectionDirty]);

  const handleInformerLinkModalOk = useCallback(() => {
    let u = (informerLinkDraft || '').trim();
    if (!u) {
      message.warning('Укажите URL');
      return;
    }
    if (!/^https?:\/\//i.test(u)) {
      u = `https://${u}`;
    }
    try {
      document.execCommand('styleWithCSS', false, false);
    } catch (_e) {
      /* ignore */
    }
    const el = cabinetInformerEditorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    const saved = informerLinkSavedRangeRef.current;
    informerLinkSavedRangeRef.current = null;
    if (saved && saved.commonAncestorContainer && saved.commonAncestorContainer.isConnected && el.contains(saved.commonAncestorContainer)) {
      try {
        sel.removeAllRanges();
        sel.addRange(saved);
      } catch (_e) {
        /* ignore */
      }
    }
    const activeRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    const hasRange = activeRange && !activeRange.collapsed;
    const safeHref = u.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    if (hasRange) {
      document.execCommand('createLink', false, u);
    } else {
      document.execCommand('insertHTML', false, `<a href="${safeHref}">ссылка</a>`);
    }
    setCabinetInformer((prev) => {
      const next = { ...prev, text: el.innerHTML };
      cabinetInformerRef.current = next;
      return next;
    });
    markSectionDirty('informer');
    setInformerLinkModalOpen(false);
    setInformerLinkDraft('');
  }, [informerLinkDraft, markSectionDirty]);

  const handleOpenSaveInformerTemplateModal = useCallback(() => {
    if (!getInformerEditorPlainText(cabinetInformer.text)) {
      message.warning('Сначала введите текст информера');
      return;
    }
    if (cabinetInformerTemplates.length >= CABINET_INFORMER_TEMPLATES_MAX) {
      message.warning(`Достигнут лимит шаблонов (${CABINET_INFORMER_TEMPLATES_MAX}). Удалите лишние.`);
      return;
    }
    setInformerSaveTemplateNameDraft('');
    setInformerSaveTemplateModalOpen(true);
  }, [cabinetInformer.text, cabinetInformerTemplates.length]);

  const handleSaveInformerTemplateModalOk = useCallback(async () => {
    const name = (informerSaveTemplateNameDraft || '').trim();
    if (!name) {
      message.warning('Укажите название шаблона');
      return;
    }
    const el = cabinetInformerEditorRef.current;
    const html = (el && el.innerHTML) || cabinetInformer.text || '';
    if (!getInformerEditorPlainText(html)) {
      message.warning('Текст информера пуст');
      return;
    }
    if (cabinetInformerTemplates.length >= CABINET_INFORMER_TEMPLATES_MAX) {
      message.warning(`Нельзя добавить: максимум ${CABINET_INFORMER_TEMPLATES_MAX} шаблонов.`);
      return;
    }
    const tempId = `tmp_inf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const nextTemplates = [
      ...cabinetInformerTemplatesRef.current,
      {
        id: tempId,
        name,
        text: html,
        enabled: false,
        dismissible: true,
        starts_at: null,
        ends_at: null,
        targetClientIds: [],
      },
    ];
    cabinetInformerTemplatesRef.current = nextTemplates;
    setCabinetInformerTemplates(nextTemplates);
    setInformerSaveTemplateModalOpen(false);
    setInformerSaveTemplateNameDraft('');
    clearInformerEditorDraft();
    try {
      await persistInformerSettings({ enabled: getInformerRootEnabled() });
      message.success('Шаблон создан. Включите «Включён», чтобы показать сообщение клиентам.');
    } catch (_error) {
      message.error('Шаблон добавлен локально, но не удалось сохранить в базу');
    }
  }, [
    informerSaveTemplateNameDraft,
    cabinetInformer.text,
    cabinetInformerTemplates.length,
    clearInformerEditorDraft,
    getInformerRootEnabled,
    persistInformerSettings,
  ]);

  const handleSaveInformerTemplateEdit = useCallback(async () => {
    const editingId = informerEditingTemplateId;
    if (!editingId) return;
    const el = cabinetInformerEditorRef.current;
    const html = (el && el.innerHTML) || cabinetInformer.text || '';
    if (!getInformerEditorPlainText(html)) {
      message.warning('Текст шаблона пуст');
      return;
    }
    const nextTemplates = cabinetInformerTemplatesRef.current.map((item) =>
      item.id === editingId ? { ...item, text: html } : item,
    );
    cabinetInformerTemplatesRef.current = nextTemplates;
    setCabinetInformerTemplates(nextTemplates);
    clearInformerEditorDraft();
    try {
      await persistInformerSettings();
      message.success('Шаблон обновлён');
    } catch (_error) {
      message.error('Не удалось сохранить изменения шаблона');
    }
  }, [
    cabinetInformer.text,
    clearInformerEditorDraft,
    informerEditingTemplateId,
    persistInformerSettings,
  ]);

  const handleEditInformerTemplate = useCallback((index) => {
    const tpl = cabinetInformerTemplatesRef.current[index];
    if (!tpl) return;
    const html = tpl.text || '';
    const el = cabinetInformerEditorRef.current;
    if (el) {
      el.innerHTML = html;
    }
    setCabinetInformer((prev) => {
      const next = { ...prev, text: html };
      cabinetInformerRef.current = next;
      return next;
    });
    setInformerEditingTemplateId(tpl.id || null);
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    el?.focus?.();
  }, []);

  const handleTemplateEnabledToggle = useCallback(
    async (index, checked) => {
      const list = cabinetInformerTemplatesRef.current;
      if (index < 0 || index >= list.length) return;
      const tpl = list[index];
      const tplId = tpl.id || `idx-${index}`;
      const nextTemplates = list.map((item, idx) =>
        idx === index ? { ...item, enabled: checked } : item,
      );
      cabinetInformerTemplatesRef.current = nextTemplates;
      setCabinetInformerTemplates(nextTemplates);
      const anyEnabled = nextTemplates.some((t) => t.enabled !== false);
      setInformerTemplateToggleLoadingId(tplId);
      try {
        await persistInformerSettings({ enabled: anyEnabled });
        message.success(checked ? 'Сообщение показывается клиентам' : 'Сообщение скрыто у клиентов');
      } catch (_error) {
        message.error('Не удалось изменить состояние шаблона');
      } finally {
        setInformerTemplateToggleLoadingId(null);
      }
    },
    [persistInformerSettings],
  );

  const handleTelegramDirty = useCallback((section) => {
    markSectionDirty(section);
  }, [markSectionDirty]);

  return {
    loading,
    sectionDirty,
    sectionSaving,
    handleSaveMarketingSection,
    handleLoadMarketingDefaults,
    cabinetInformer,
    cabinetInformerTemplates,
    cabinetInformerPublications,
    cabinetInformerEditorRef,
    informerClearPublicationsLoading,
    informerClientSelectOptions,
    informerClientsListLoading,
    informerEditingTemplateId,
    informerTemplateToggleLoadingId,
    informerTemplateDismissibleLoadingId,
    informerTemplateBodyExpanded,
    setInformerTemplateBodyExpanded,
    informerLinkModalOpen,
    setInformerLinkModalOpen,
    informerLinkDraft,
    setInformerLinkDraft,
    informerEmojiPickerOpen,
    informerSaveTemplateModalOpen,
    setInformerSaveTemplateModalOpen,
    informerSaveTemplateNameDraft,
    setInformerSaveTemplateNameDraft,
    handleClearInformerPublicationsClick,
    handleAddInformerTemplate,
    handleUpdateInformerTemplate,
    handleDeleteInformerTemplate,
    handlePersistInformerTemplateRow,
    handleInformerEditorInput,
    captureInformerLinkEditorSelection,
    captureInformerEmojiEditorSelection,
    handleInsertInformerEmoji,
    handleInformerEmojiPickerOpenChange,
    runInformerEditorCommand,
    handleInformerLinkModalOk,
    handleOpenSaveInformerTemplateModal,
    handleSaveInformerTemplateModalOk,
    handleSaveInformerTemplateEdit,
    handleEditInformerTemplate,
    handleTemplateEnabledToggle,
    clearInformerEditorDraft,
    cabinetTelegram,
    patchCabinetTelegram,
    mergeInformerAudienceOptions,
    handleTelegramDirty,
    markSectionDirty,
  };
};

export default useCabinetMarketingSettings;
